import pluralize from 'pluralize';
import React, { useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import type { HistoricalResponse } from '@core/types/metrics';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Sparkline from '@ui/Sparkline';

import classes from './style.module.scss';
import './style.scss';

interface Props {
  /**
   * Base path used to generate link paths correctly. Typically sourced from our
   * `BaseUrlContext` which defaults to `/`, this ensures we navigate users
   * within the same project version and child subdomain.
   * @example
   * ```ts
   * '/'
   * '/child-project/v2.0'
   * ```
   */
  baseUrl: string;
  className?: string;
  /** The logs graph data */
  data?: HistoricalResponse;
  /** Whether the graph response is loading */
  isLoading?: boolean;
  /** Whether the user is logged in */
  isLoggedIn?: boolean;
  /** Whether Dev Dash in Hub is enabled (to show My Requests page) */
  myRequestsEnabled?: boolean;
  style?: React.CSSProperties;
  /** Whether to use dark mode */
  useDarkMode?: boolean;
}

const APILogsGraph = React.memo(function APILogsGraph({
  baseUrl,
  className,
  data,
  isLoading = false,
  isLoggedIn = false,
  style,
  myRequestsEnabled = false,
  useDarkMode = false,
}: Props) {
  const bem = useClassy(classes, 'APILogInfo');
  const isDataEmpty = data && (!Object.keys(data).length || data?.total === 0);

  const requestsThisMonth = useMemo(() => {
    if (isLoading) return '';

    const count = data?.total || 0;

    return `${pluralize('Request', count, true)} This Month`;
  }, [data, isLoading]);

  const seeAllRequestsLink = useMemo(() => {
    if (!isLoggedIn || !myRequestsEnabled) return undefined;
    const path = '/reference/intro/my-requests';
    return baseUrl === '/' ? path : `${baseUrl}${path}`;
  }, [baseUrl, isLoggedIn, myRequestsEnabled]);

  return (
    <Flex
      align="center"
      className={bem('&', 'rm-APILogInfo', className)}
      data-testid="api-logs-graph"
      style={style}
      // We set the `translate="no"` attribute on this element to prevent sending sensitive
      // auth information to translation services.
      translate="no"
    >
      {!!data && (
        <div className={bem('-sparkline')}>
          <Sparkline
            data={(isDataEmpty ? {} : data.dataSet.data) as Record<string, number[]>}
            height="20px"
            labels={isDataEmpty ? [] : data.labels}
            maxWidth="80px"
            useDarkMode={useDarkMode}
          />
        </div>
      )}

      <span className={bem('-requests-count')}>{requestsThisMonth}</span>
      {!!myRequestsEnabled && (
        <Button disabled={!isLoggedIn} ghost href={seeAllRequestsLink} kind="secondary" size="xs">
          See All Requests <Icon aria-hidden="true" name="arrow-right" />
        </Button>
      )}
    </Flex>
  );
});

export default APILogsGraph;
